import React, { useState, useEffect } from "react";
import { useSelector } from 'react-redux';
import { getCookie } from '../../util.js';

import {
  Pane,
  Paragraph,
  Button,
  TextInput,
  toaster,
  FormField,
  Select,
  Card,
  Spinner,
  Text
} from 'evergreen-ui';

function GroupUserInvite(props) {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const currentUser = useSelector((state) => state.user.value);
  const pubusers = useSelector((state) => state.pubusers?.value || []);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("2"); // Default to "Admin" role
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [authGroups, setAuthGroups] = useState([]);

  // Get the current user's role to determine permissions
  const currentUserData = pubusers.find(user => parseInt(user.user.id) === parseInt(currentUser));
  const userRole = currentUserData?.role?.name || "";

  // Check if user has admin privileges (owner or admin)
  const hasAdminPrivileges = userRole === "owner" || userRole === "admin";

  // Fetch auth_group roles on component mount
  useEffect(() => {
    const fetchAuthGroups = async () => {
      try {
        const response = await fetch('/api/auth_groups/');
        const data = await response.json();
        if (data && Array.isArray(data)) {
          setAuthGroups(data);
        }
      } catch (error) {
        console.error("Error fetching auth groups:", error);
      }
    };

    fetchAuthGroups();
  }, []);

  // Basic email validation
  const isValidEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleInviteUser = () => {
    // Reset error state
    setError("");

    // Validate email
    if (!email) {
      setError("Email is required");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    fetch('/send_invitation/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken
      },
      body: JSON.stringify({
        "email": email,
        "publisher": currentpublisher.id,
        "role": role,
        "use_auth_group": true // Flag to use auth_group instead of UserRole
      })
    })
    .then(res => res.json())
    .then(res => {
      setIsLoading(false);
      if (res.status === 200) {
        toaster.success('Invitation sent to ' + email);
        setEmail("");
        props.addNewInvitation(email, res.key);
      } else {
        setError(res.message || 'There was an error sending your invitation');
        toaster.danger(res.message || 'There was an error sending your invitation');
      }
    })
    .catch(err => {
      setIsLoading(false);
      setError('Network error. Please try again.');
      toaster.danger('Network error. Please try again.');
    });
  }

  // Use auth_group roles if available, otherwise fall back to UserRole
  const roleOptions = authGroups.length > 0
    ? authGroups.map(group => ({
        value: group.id.toString(),
        label: group.name
      }))
    : props.userroles
        .filter(role => role.name !== "owner")
        .map(role => ({
          value: role.id,
          label: role.name
        }));

  // Get role descriptions
  const getRoleDescription = (roleName) => {
    if (roleName === "Super Admin") {
      return "Has access to all features.";
    } else if (roleName === "Admin") {
      return "Has access to all features except for settings customization, team management, and billing.";
    }
    return "";
  };

  // If user doesn't have admin privileges, don't render the invite form
  if (!hasAdminPrivileges) {
    return null;
  }

  return (
    <Card
      padding={24}
      backgroundColor="white"
      elevation={0}
      border="muted"
    >
      <Paragraph size={500} fontWeight="bold" marginBottom={16}>Invite a new team member</Paragraph>

      <FormField
        label="Email address"
        labelFor="email-input"
        validationMessage={error ? error : null}
        isRequired
      >
        <TextInput
          id="email-input"
          onChange={e => setEmail(e.target.value)}
          value={email}
          width="100%"
          placeholder="colleague@example.com"
          isInvalid={!!error}
        />
      </FormField>

      <FormField
        label="Role"
        labelFor="role-select"
        marginTop={16}
        marginBottom={8}
      >
        <Select
          id="role-select"
          value={role}
          onChange={e => setRole(e.target.value)}
          width="100%"
        >
          {roleOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </FormField>

      {/* Role description */}
      {role && (
        <Text display="block" size={300} color="muted" marginBottom={24}>
          {getRoleDescription(roleOptions.find(option => option.value.toString() === role.toString())?.label)}
        </Text>
      )}

      <Pane display="flex" justifyContent="flex-start">
        <Button
          intent="success"
          marginRight={16}
          onClick={handleInviteUser}
          disabled={isLoading}
        >
          {isLoading ? <Spinner size={16} marginRight={8} /> : null}
          Send Invitation
        </Button>
        <Button
          onClick={() => {
            setEmail("");
            setError("");
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </Pane>
    </Card>
  )
}

export default GroupUserInvite;
